import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api/client';
import type { UserProfile } from '../settings/types';
import { userDisplayName } from '../settings/userDisplay';

export function usePdfViewerIdentity(enabled: boolean) {
  const [userId, setUserId] = useState('unknown');
  const [userName, setUserName] = useState('User');
  const [hasPhoto, setHasPhoto] = useState<boolean | null>(null);
  const [identityAliases, setIdentityAliases] = useState<string[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch('/api/auth/profile');
        if (!res.ok) return;
        const profile = (await res.json()) as UserProfile & Record<string, unknown>;
        if (cancelled) return;
        const objectId = String(profile.objectId ?? profile.ObjectId ?? '').trim();
        const mail = String(profile.mail ?? profile.Mail ?? '').trim();
        const upn = String(profile.userPrincipalName ?? profile.UserPrincipalName ?? '').trim();
        const id = objectId || mail || upn || 'unknown';
        const aliases = [objectId, mail, upn].filter(Boolean);
        setUserId(id);
        setIdentityAliases(aliases);
        setUserName(userDisplayName(upn || mail, profile.displayName));
        setHasPhoto(profile.hasPhoto ?? null);
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const userAliases = useMemo(() => {
    const ids = new Set<string>();
    for (const value of identityAliases) {
      if (value) ids.add(value);
    }
    if (userId !== 'unknown') ids.add(userId);
    return ids;
  }, [identityAliases, userId]);

  return { userId, userName, hasPhoto, userAliases };
}
